import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function AboutPage() {
    const { t } = useTranslation('common');

    const jobOpenings = [
        {
            id: 1,
            role: t('about.careers.positions.advisor.role'),
            href: '#',
            description: t('about.careers.positions.advisor.description'),
            location: t('about.careers.positions.advisor.location'),
        },
        {
            id: 2,
            role: t('about.careers.positions.agent.role'),
            href: '#',
            description: t('about.careers.positions.agent.description'),
            location: t('about.careers.positions.agent.location'),
        },
        {
            id: 3,
            role: t('about.careers.positions.support.role'),
            href: '#',
            description: t('about.careers.positions.support.description'),
            location: t('about.careers.positions.support.location'),
        },
    ];

    return (
        <div className="bg-white">
            <main className="isolate">
                {/* Hero section */}
                <div className="relative isolate -z-10 overflow-hidden bg-gradient-to-b from-sky-100/20 pt-14">
                    <div
                        aria-hidden="true"
                        className="absolute inset-y-0 right-1/2 -z-10 -mr-96 w-[200%] origin-top-right skew-x-[-30deg] bg-white shadow-xl shadow-sky-600/10 ring-1 ring-sky-50 sm:-mr-80 lg:-mr-96"
                    />
                    <div className="mx-auto max-w-7xl px-6 py-10 sm:py-40 lg:px-8">
                        <div className="mx-auto max-w-2xl lg:mx-0 lg:grid lg:max-w-none lg:grid-cols-2 lg:gap-x-16 lg:gap-y-8 xl:grid-cols-1 xl:grid-rows-1 xl:gap-x-8">
                            <h1 className="max-w-2xl text-balance text-5xl font-semibold tracking-tight text-gray-900 sm:text-7xl lg:col-span-2 xl:col-auto">
                                {t('about.hero.title')}
                            </h1>
                            <div className="mt-6 max-w-xl lg:mt-0 xl:col-end-1 xl:row-start-1">
                                <p className="text-pretty text-lg font-medium text-gray-500 sm:text-xl/8">
                                    {t('about.hero.subtitle')}
                                </p>
                            </div>
                            <img
                                alt={t('about.hero.imageAlt')}
                                src="https://images.pexels.com/photos/723465/pexels-photo-723465.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                                className="mt-10 aspect-[6/5] w-full max-w-lg rounded-2xl object-cover sm:mt-16 lg:mt-0 lg:max-w-none xl:row-span-2 xl:row-end-2 xl:mt-36"
                            />
                        </div>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 -z-10 h-24 bg-gradient-to-t from-white sm:h-32" />
                </div>

                {/* Logo cloud */}
                <div className="mx-auto mt-32 max-w-7xl sm:mt-40 sm:px-6 lg:px-8">
                    <div className="relative isolate overflow-hidden bg-gray-900 px-6 py-24 text-center shadow-2xl sm:rounded-3xl sm:px-16">
                        <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
                            {t('about.testimonials.title')}
                        </h2>
                        <p className="mx-auto mt-6 max-w-xl text-lg/8 text-gray-300">
                            {t('about.testimonials.description')}
                        </p>
                    </div>
                </div>

                {/* Content section */}
                <div className="mt-32 overflow-hidden sm:mt-40">
                    <div className="mx-auto max-w-7xl px-6 lg:flex lg:px-8">
                        <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-12 gap-y-16 lg:mx-0 lg:min-w-full lg:max-w-none lg:flex-none lg:gap-y-8">
                            <div className="lg:col-end-1 lg:w-full lg:max-w-lg lg:pb-8">
                                <h2 className="text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                                    {t('about.team.title')}
                                </h2>
                                <p className="mt-6 text-xl/8 text-gray-600">
                                    {t('about.team.description.main')}
                                </p>
                                <p className="mt-6 text-xl/8 text-gray-600">
                                    {t('about.team.description.secondary')}
                                </p>
                            </div>
                            <div className="flex flex-wrap items-start justify-end gap-6 sm:gap-8 lg:contents">
                                <div className="hidden md:block w-0 flex-auto lg:ml-auto lg:w-auto lg:flex-none lg:self-end">
                                    <img
                                        alt=""
                                        src="https://images.pexels.com/photos/54097/spain-flag-flutter-spanish-54097.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                                        className="aspect-[7/5] w-[37rem] max-w-none rounded-2xl bg-gray-50 object-cover"
                                    />
                                </div>
                                <div className="contents lg:col-span-2 lg:col-end-2 lg:ml-auto lg:flex lg:w-[37rem] lg:items-start lg:justify-end lg:gap-x-8">
                                    <div className="order-first flex w-64 flex-none justify-end self-end lg:w-auto">
                                        <img
                                            alt=""
                                            src="https://images.pexels.com/photos/541217/pexels-photo-541217.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                                            className="left-2 md:left-0 relative aspect-[4/3] w-[24rem] max-w-none flex-none rounded-2xl bg-gray-50 object-cover"
                                        />
                                    </div>
                                    <div className="hidden md:flex w-96 flex-auto justify-end lg:w-auto lg:flex-none">
                                        <img
                                            alt=""
                                            src="https://images.pexels.com/photos/1703311/pexels-photo-1703311.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                                            className="aspect-[7/5] w-[37rem] max-w-none flex-none rounded-2xl bg-gray-50 object-cover"
                                        />
                                    </div>
                                    <div className="hidden sm:block sm:w-0 sm:flex-auto lg:w-auto lg:flex-none">
                                        <img
                                            alt=""
                                            src="https://images.pexels.com/photos/1338393/pexels-photo-1338393.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                                            className="aspect-[4/3] w-[24rem] max-w-none rounded-2xl bg-gray-50 object-cover"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div className="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:px-8">
                    <div className="mx-auto max-w-2xl lg:mx-0">
                        <h2 className="text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                            {t('about.stats.first.title')}
                        </h2>
                        <p className="mt-6 text-base/7 text-gray-600">
                            {t('about.stats.first.subtitle')}
                        </p>
                    </div>
                    <div className="mx-auto mt-16 flex max-w-2xl flex-col gap-8 lg:mx-0 lg:mt-20 lg:max-w-none lg:flex-row lg:items-end">
                        <div className="flex flex-col-reverse justify-between gap-x-16 gap-y-8 rounded-2xl bg-gray-50 p-8 sm:w-3/4 sm:max-w-md sm:flex-row-reverse sm:items-end lg:w-72 lg:max-w-none lg:flex-none lg:flex-col lg:items-start">
                            <p className="flex-none text-3xl font-bold tracking-tight text-gray-900">
                                {t('about.stats.items.satisfied.number')}
                            </p>
                            <div className="sm:w-80 sm:shrink lg:w-auto lg:flex-none">
                                <p className="text-lg font-semibold tracking-tight text-gray-900">
                                    {t('about.stats.items.satisfied.title')}
                                </p>
                                <p className="mt-2 text-base/7 text-gray-600">
                                    {t('about.stats.items.satisfied.description')}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col-reverse justify-between gap-x-16 gap-y-8 rounded-2xl bg-gray-900 p-8 sm:flex-row-reverse sm:items-end lg:w-full lg:max-w-sm lg:flex-auto lg:flex-col lg:items-start lg:gap-y-44">
                            <p className="flex-none text-3xl font-bold tracking-tight text-white">
                                {t('about.stats.items.service.number')}
                            </p>
                            <div className="sm:w-80 sm:shrink lg:w-auto lg:flex-none">
                                <p className="text-lg font-semibold tracking-tight text-white">
                                    {t('about.stats.items.service.title')}
                                </p>
                                <p className="mt-2 text-base/7 text-gray-400">
                                    {t('about.stats.items.service.description')}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col-reverse justify-between gap-x-16 gap-y-8 rounded-2xl bg-sky-600 p-8 sm:w-11/12 sm:max-w-xl sm:flex-row-reverse sm:items-end lg:w-full lg:max-w-none lg:flex-auto lg:flex-col lg:items-start lg:gap-y-28">
                            <p className="flex-none text-3xl font-bold tracking-tight text-white">
                                {t('about.stats.items.experience.number')}
                            </p>
                            <div className="sm:w-80 sm:shrink lg:w-auto lg:flex-none">
                                <p className="text-lg font-semibold tracking-tight text-white">
                                    {t('about.stats.items.experience.title')}
                                </p>
                                <p className="mt-2 text-base/7 text-sky-200">
                                    {t('about.stats.items.experience.description')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Careers section */}
                <div className="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:px-8">
                    <div className="mx-auto flex max-w-2xl flex-col items-end justify-between gap-16 lg:mx-0 lg:max-w-none lg:flex-row">
                        <div className="w-full lg:max-w-lg lg:flex-auto">
                            <h2 className="text-pretty text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
                                {t('about.careers.title')}
                            </h2>
                            <p className="mt-6 text-xl/8 text-gray-600">
                                {t('about.careers.subtitle')}
                            </p>
                            <img
                                alt={t('about.careers.imageAlt')}
                                src="https://images.pexels.com/photos/723465/pexels-photo-723465.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                                className="mt-16 aspect-[6/5] w-full rounded-2xl bg-gray-50 object-cover lg:aspect-auto lg:h-[34.5rem]"
                            />
                        </div>
                        <div className="w-full lg:max-w-xl lg:flex-auto">
                            <h3 className="sr-only">{t('about.careers.positions.title')}</h3>
                            <ul className="-my-8 divide-y divide-gray-100">
                                {jobOpenings.map((position) => (
                                    <li key={position.id} className="py-8">
                                        <dl className="relative flex flex-wrap gap-x-3">
                                            <dt className="sr-only">{t('about.careers.positions.roleLabel')}</dt>
                                            <dd className="w-full flex-none text-lg font-semibold tracking-tight text-gray-900">
                                                {position.role}
                                            </dd>
                                            <dt className="sr-only">{t('about.careers.positions.descriptionLabel')}</dt>
                                            <dd className="mt-2 w-full flex-none text-base/7 text-gray-600">
                                                {position.description}
                                            </dd>
                                            <dt className="sr-only">{t('about.careers.positions.locationLabel')}</dt>
                                            <dd className="mt-4 flex items-center gap-x-3 text-base/7 text-gray-500">
                                                <svg viewBox="0 0 2 2" aria-hidden="true"
                                                     className="size-0.5 flex-none fill-gray-300">
                                                    <circle r={1} cx={1} cy={1}/>
                                                </svg>
                                                {position.location}
                                            </dd>
                                        </dl>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Second Stats section */}
                <div className="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:px-8">
                    <div className="mx-auto max-w-2xl lg:mx-0">
                        <h2 className="text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                            {t('about.stats.second.title')}
                        </h2>
                        <p className="mt-6 text-base/7 text-gray-600">
                            {t('about.stats.second.subtitle')}
                        </p>
                    </div>
                    <div
                        className="mx-auto mt-16 flex max-w-2xl flex-col gap-8 lg:mx-0 lg:mt-20 lg:max-w-none lg:flex-row lg:items-end">
                        <div
                            className="flex flex-col-reverse justify-between gap-x-16 gap-y-8 rounded-2xl bg-gray-50 p-8 sm:w-3/4 sm:max-w-md sm:flex-row-reverse sm:items-end lg:w-72 lg:max-w-none lg:flex-none lg:flex-col lg:items-start">
                            <p className="flex-none text-3xl font-bold tracking-tight text-gray-900">
                                {t('about.stats.items.properties.number')}
                            </p>
                            <div className="sm:w-80 sm:shrink lg:w-auto lg:flex-none">
                                <p className="text-lg font-semibold tracking-tight text-gray-900">
                                    {t('about.stats.items.properties.title')}
                                </p>
                                <p className="mt-2 text-base/7 text-gray-600">
                                    {t('about.stats.items.properties.description')}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col-reverse justify-between gap-x-16 gap-y-8 rounded-2xl bg-gray-900 p-8 sm:flex-row-reverse sm:items-end lg:w-full lg:max-w-sm lg:flex-auto lg:flex-col lg:items-start lg:gap-y-44">
                            <p className="flex-none text-3xl font-bold tracking-tight text-white">
                                {t('about.stats.items.offices.number')}
                            </p>
                            <div className="sm:w-80 sm:shrink lg:w-auto lg:flex-none">
                                <p className="text-lg font-semibold tracking-tight text-white">
                                    {t('about.stats.items.offices.title')}
                                </p>
                                <p className="mt-2 text-base/7 text-gray-400">
                                    {t('about.stats.items.offices.description')}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col-reverse justify-between gap-x-16 gap-y-8 rounded-2xl bg-sky-600 p-8 sm:w-11/12 sm:max-w-xl sm:flex-row-reverse sm:items-end lg:w-full lg:max-w-none lg:flex-auto lg:flex-col lg:items-start lg:gap-y-28">
                            <p className="flex-none text-3xl font-bold tracking-tight text-white">
                                {t('about.stats.items.support.number')}
                            </p>
                            <div className="sm:w-80 sm:shrink lg:w-auto lg:flex-none">
                                <p className="text-lg font-semibold tracking-tight text-white">
                                    {t('about.stats.items.support.title')}
                                </p>
                                <p className="mt-2 text-base/7 text-sky-200">
                                    {t('about.stats.items.support.description')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA section */}
                <div className="relative isolate -z-10 mt-32 sm:mt-40">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                        <div className="mx-auto max-w-2xl text-center">
                            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                {t('about.cta.title')}
                            </h2>
                            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-600">
                                {t('about.cta.subtitle')}
                            </p>
                            <div className="mt-10 flex items-center justify-center gap-x-6">
                                <Link
                                    to="/contact"
                                    className="rounded-md bg-sky-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                                >
                                    {t('about.cta.buttons.contact')}
                                </Link>
                                <Link to="/listings" className="text-sm font-semibold leading-6 text-gray-900">
                                    {t('about.cta.buttons.listings')} <span aria-hidden="true">→</span>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}